import React, { useState } from 'react';
import { apiService } from '../services/api';
import { useApi, useMutation } from '../hooks/useApi';
import logger from '../utils/logger';
import { ListChecks, PlusCircle, Pencil, Trash2 } from 'lucide-react';

function Kategorite() {
  const [form, setForm] = useState({ emri: '', emri_mk: '' });
  const [editId, setEditId] = useState(null);

  const { data: kategoriteData, execute: refetchKategorite } = useApi(
    () => apiService.get('/kategorite'),
    [],
    { immediate: true, showNotifications: false }
  );

  const kategorite = Array.isArray(kategoriteData) ? kategoriteData : (kategoriteData?.data || []);

  const { mutate: saveKategori, loading: saving } = useMutation(
    async (data) => {
      if (editId) {
        return await apiService.put(`/kategorite/${editId}`, data);
      } else {
        return await apiService.post('/kategorite', data);
      }
    },
    {
      successMessage: editId ? 'Kategoria u përditësua me sukses!' : 'Kategoria u shtua me sukses!',
      onSuccess: () => {
        setForm({ emri: '', emri_mk: '' });
        setEditId(null);
        refetchKategorite();
      }
    }
  );

  const { mutate: deleteKategori } = useMutation(
    async (id) => {
      return await apiService.delete(`/kategorite/${id}`);
    },
    {
      successMessage: 'Kategoria u fshi me sukses!',
      onSuccess: () => {
        refetchKategorite();
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    saveKategori(form);
  };

  const handleEdit = (kategori) => {
    setEditId(kategori.id);
    setForm({ emri: kategori.emri, emri_mk: kategori.emri_mk });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('A je i sigurt që do ta fshish këtë kategori?')) return;
    deleteKategori(id);
  };

  return (
    <div className="w-full animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <ListChecks size={32} className="text-primary-600 dark:text-primary-400" />
        </div>
        Kategoritë e Produkteve
      </h2>

      <form onSubmit={handleSubmit} className="card-premium p-6 mb-8 flex gap-4">
        <input
          type="text"
          value={form.emri}
          onChange={(e) => setForm({ ...form, emri: e.target.value })}
          placeholder="Emri (shqip)"
          className="input-premium flex-1"
        />
        <input
          type="text"
          value={form.emri_mk}
          onChange={(e) => setForm({ ...form, emri_mk: e.target.value })}
          placeholder="Emri (maqedonisht)"
          className="input-premium flex-1"
        />
        <button
          type="submit"
          disabled={saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusCircle size={18} />
          {saving ? 'Po ruhet...' : (editId ? 'Përditëso' : 'Shto')}
        </button>
      </form>

      {/* Desktop view */}
      <div className="hidden md:block card-premium overflow-hidden">
        <table className="table-premium">
          <thead>
            <tr>
              <th>#</th>
              <th>Emri (shqip)</th>
              <th>Emri (maqedonisht)</th>
              <th>Veprimet</th>
            </tr>
          </thead>
          <tbody>
            {kategorite.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nuk ka kategori të regjistruar
                </td>
              </tr>
            ) : (
              kategorite.map((k, i) => (
                <tr key={k.id}>
                  <td>{i + 1}</td>
                  <td className="font-medium">{k.emri}</td>
                  <td className="italic text-gray-600 dark:text-gray-400">{k.emri_mk}</td>
                  <td className="space-x-2">
                    <button onClick={() => handleEdit(k)} className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-300 inline-flex items-center gap-1 transition-colors">
                      <Pencil size={16} /> Edito
                    </button>
                    <button onClick={() => handleDelete(k.id)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 inline-flex items-center gap-1 transition-colors">
                      <Trash2 size={16} /> Fshi
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile view – karta vertikale */}
      <div className="md:hidden grid gap-4">
        {kategorite.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <ListChecks size={64} className="text-gray-300 dark:text-dark-600" />
            </div>
            <h3 className="empty-state-title">Nuk ka kategori</h3>
            <p className="empty-state-description">Shto kategori të re për të filluar</p>
          </div>
        ) : (
          kategorite.map((k, i) => (
            <div key={k.id} className="card-premium p-4 space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400"># {i + 1}</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">{k.emri}</div>
              <div className="italic text-gray-600 dark:text-gray-400">{k.emri_mk}</div>
              <div className="flex gap-4 mt-3">
                <button onClick={() => handleEdit(k)} className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-300 inline-flex items-center gap-1 transition-colors">
                  <Pencil size={16} /> Edito
                </button>
                <button onClick={() => handleDelete(k.id)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 inline-flex items-center gap-1 transition-colors">
                  <Trash2 size={16} /> Fshi
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Kategorite;
